import { filter, Subject, Subscription } from 'rxjs';
import { RetryableWebSocket, TOO_MANY_WEBSOCKET_CONNECTIONS, withoutResetNotifications } from './retryable.websocket';
import { URLS } from './app.constant';
import { defined } from '../utils/runtypes';
import { ResourceResponse } from '../utils/utils';
import { LoginSuccess } from './auth.service';

export type ConfirmLoginRequest = {
  LoginRequest: {
    emailId: string;
    password: string;
    confirmationToken: string;
    paymentIFrame: boolean;
  };
};

export type ConfirmTransactionRequest = {
  ConfirmTransactionRequest: {
    jwt: string;
    transactionId: string;
    title: string;
    summary: string;
  };
};

export type ConfirmLoginResponse = {
  success: boolean;
  errorMessage: string | null;
  confirmationCode: string | null;
  data: ResourceResponse<LoginSuccess> | null;
};

export type ConfirmTransactionResponse = {
  success: boolean;
  errorMessage: string | null;
  confirmationCode: string | null;
  data: string | null;
};

const INVALID_REQUEST = 4002;
const PUSH_FAILED = 4003;
const EXPECTED_FATAL_ERROR_CODES = [TOO_MANY_WEBSOCKET_CONNECTIONS, INVALID_REQUEST, PUSH_FAILED];

/**
 * Wraps the logic around the temporary websocket opened when doing biometric 2FA using our mobile app.
 * This socket only needs to be open for the duration of the login process so the life cycle isn't
 * in a singleton service.
 */
export class AuthBiometricWebSocket<In, Out> {
  private _websocket: RetryableWebSocket<In, Out> | null = null;
  private _subscription: Subscription | null = null;
  private closed = false;
  private jsonRequest: Out;

  private readonly confirmResponse = new Subject<In>();
  public readonly confirmResponse$ = this.confirmResponse.asObservable();

  public sendRequest(request: Out): void {
    this.jsonRequest = request;
    this.doSendRequest();
  }

  // public sendTransactionRequest(jwt: string, transactionId: Guid, title: string, summary: string): void {
  //   this.jsonRequest = {
  //     ConfirmTransactionRequest: { jwt, transactionId: transactionId.toString(), title, summary },
  //   };
  //   this.sendRequest();
  // }

  private doSendRequest(): void {
    this._websocket = new RetryableWebSocket<In, Out>(
      URLS.biometric2FASocket,
      () => this._websocket?.send(this.jsonRequest),
      (code?: number) => defined(code) && !EXPECTED_FATAL_ERROR_CODES.includes(code) && !this.closed
    );

    this._subscription = this._websocket
      .observe()
      .pipe(filter(withoutResetNotifications))
      .subscribe(this.onBiometricWebSocketMessage.bind(this));
  }

  public resend() {
    this._websocket?.send(this.jsonRequest);
  }

  public disconnect(): void {
    console.log('Closing biometric socket');
    this._subscription?.unsubscribe();
    this._websocket?.close();
    this._subscription = null;
    this._websocket = null;
    this.closed = true;
    console.log('Closed biometric socket');
  }

  private onBiometricWebSocketMessage(responseData: In): void {
    console.log(`got biometric socket msg: ${responseData}`);
    this.confirmResponse.next(responseData);
  }
}
